"use client";

import Card from "@/components/atoms/Card";
import FooterNavigationLinks from "@/components/organisms/HomeNewFooter/FooterNavigationLinks";
import FooterCityLinks, {
  type FooterCity,
} from "@/components/organisms/HomeNewFooter/FooterCityLinks";
import styles from "./footer.module.scss";
import UnivoImage from "@/components/common/UnivoImage";
import UnivoLink from "@/components/common/UnivoLink";
import { IMAGE_ASSETS } from "@/constants/images";
import cn from "clsx";
import NewsletterForm from "@molecules/NewsletterForm/NewsletterForm";
import { pushToDataLayer } from "@/helpers/gtag";
import { getGlobalStateValue } from "@/context/utils";
import X from "@/SvgIcons/X";
import Facebook from "@/SvgIcons/Facebook";
import Linkedin from "@/SvgIcons/Linkedin";
import Instagram from "@/SvgIcons/Instagram";
import Youtube from "@/SvgIcons/Youtube";
import { usePathname } from "next/navigation";
import type { SiteFooterData } from "@/features/site-chrome/model/siteChrome.types";
import { resolveDeviceLogo } from "@/features/site-chrome/model/siteLogo";
import { useCommonData } from "@/context/CommonDataContext";

/** Static newsletter (not CMS-driven); NewsletterForm's `data` shape. */
const NEWSLETTER = {
  heading: "Newsletter",
  form: {
    EmailPlaceholder: [{ enabled: true, text: "Email ID" }],
    NewsLetterFormCTA: { enabled: true, text: "Subscribe" },
  },
};

export default function FooterNew({
  footerData,
  isSponsoredCertificates = false,
  isfooterNew=false,
  cityListingPages = null,
  device: deviceProp,
}: {
  footerData?: SiteFooterData | null;
  isSponsoredCertificates?: boolean;
  isfooterNew?:boolean;
  cityListingPages?: FooterCity[] | null;
  device?: string;
}) {
  const fireWebEngage = (source: string) => {
    window?.webengage?.track("Social Media Icon Clicked", {
      "Social Media Name": source,
    });
  };

  const GAEventsHandler = () => {
    pushToDataLayer({
      event: "logo_click",
      section_name: "footer section",
      page_type: getGlobalStateValue("pageType"),
    });
  };

  const footer = footerData?.footer;
  const { device: contextDevice } = useCommonData();
  const device = deviceProp ?? contextDevice;
  // Mweb → mobileLogo only, Dweb → desktopLogo only (no cross-device
  // fallback); no CMS image for this device → no logo.
  const cmsLogo = resolveDeviceLogo(footer?.logo, device);

  const GAEventsLogoHander = ({ title }) => {
    pushToDataLayer({
      event: "social_media_interaction",
      section_name: footer?.social?.heading || "",
      cta_text: title,
      page_type: getGlobalStateValue("pageType"),
    });
    fireWebEngage(title);
  };

  const SocialLogo = {
    X: <X />,
    Facebook: <Facebook />,
    Linkedin: <Linkedin />,
    Instagram: <Instagram />,
    youtube: <Youtube />,
  };

  const pathName = usePathname();

  return (
    <>
      <footer className={cn(styles.root,{[styles.noMargin]:isfooterNew})}
      style={{
        marginTop: pathName === "/about-us" ? 0 : undefined,
      }}>
        <Card
          noRadius={true}
          shadow={false}
          isBlue={true}
          extraClasses={cn("px-3 lg:px-20", styles.container)}
        >
          <FooterNavigationLinks footerNavigation={footerData?.footerNavigation ?? []}>
            <FooterCityLinks cities={cityListingPages} device={device} />
          </FooterNavigationLinks>

          {/* Logo section */}
          <div className={styles.footerBottomSection}>
            {footer?.logo && (
              <div className={cn(styles.logo, "h-full")}>
                {cmsLogo?.src && (
                  <UnivoLink
                    onClick={GAEventsHandler}
                    href="/"
                    id="amity-online"
                  >
                    <UnivoImage
                      src={cmsLogo.src}
                      // CMS host may not be in images.remotePatterns — serve it as-is.
                      unoptimized
                      alt={cmsLogo.alt || "amity online"}
                      width={200}
                      height={65}
                      sizes="200px"
                      id="amity-logo"
                      loading="lazy"
                      fetchPriority="low"
                    />
                  </UnivoLink>
                )}
                <span>
                  India's No.1 Online University offering UG, PG, and
                  certification programs across various disciplines.
                </span>
              </div>
            )}

            {/* Middle login section */}
            <div className={cn(styles.footermiddle, "h-full")}>
              {/* Contact section */}
              {footer?.contact ? (
                <div className={cn(styles.allcontent)}>
                  {isSponsoredCertificates ? (
                    <div
                      className={cn(
                        "text-blue-950 font-bold font-raleway",
                        styles.heading
                      )}
                    >
                      For Payment Related Issues
                    </div>
                  ) : footer.contact.heading ? (
                    <div className={styles.heading} id="toll-free-title">
                      {footer.contact.heading}
                    </div>
                  ) : null}

                  {isSponsoredCertificates ? (
                    <div className="mt-2 block">
                      <div
                        className={cn("flex items-center", styles.contactBox)}
                      >
                        <a
                          onClick={() => {
                            pushToDataLayer({
                              event: "footer_interaction",
                              cta_text: " payments@amityonline.com",
                              section_name: "For Payment Related Issues.",
                              page_type: getGlobalStateValue("pageType"),
                            });
                          }}
                          href={"mailto:payments@amityonline.com"}
                          className={cn(styles.contactSponsored)}
                        >
                          <svg width={20} height={20} viewBox="0 0 512 512">
                            {/*!Font Awesome Free 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2025 Fonticons, Inc.*/}
                            <path d="M48 64C21.5 64 0 85.5 0 112c0 15.1 7.1 29.3 19.2 38.4L236.8 313.6c11.4 8.5 27 8.5 38.4 0L492.8 150.4c12.1-9.1 19.2-23.3 19.2-38.4c0-26.5-21.5-48-48-48L48 64zM0 176L0 384c0 35.3 28.7 64 64 64l384 0c35.3 0 64-28.7 64-64l0-208L294.4 339.2c-22.8 17.1-54 17.1-76.8 0L0 176z" />
                          </svg>{" "}
                          payments@amityonline.com
                        </a>
                      </div>
                    </div>
                  ) : (
                    footer.contact.items.map(
                      (contact, i) => (
                        <div className={styles.contactsection} key={i}>
                          <p className={cn(styles.subHeading)}>
                            {contact.title}
                          </p>
                          <div
                            className={cn(
                              "flex items-center",
                              styles.contactBox
                            )}
                          >
                            <UnivoImage
                              src={IMAGE_ASSETS.footerPhoneIcon}
                              alt="Phone Icon"
                              className="w-5 h-5 mr-2"
                              width={20}
                              height={20}
                              sizes="20px"
                              loading="lazy"
                              fetchPriority="low"
                              unoptimized
                            />
                            <a
                              onClick={() => {
                                pushToDataLayer({
                                  event: "footer_interaction",
                                  cta_text: contact.title.includes("Student")
                                    ? "Student support toll free number"
                                    : "Admission Toll Free number",
                                  section_name: "Toll free numbers",
                                  page_type: getGlobalStateValue("pageType"),
                                });
                              }}
                              href={contact.href}
                              target={contact.target}
                              className={cn(styles.contact)}
                            >
                              {contact.label}
                            </a>
                          </div>
                        </div>
                      )
                    )
                  )}
                </div>
              ) : null}
            </div>

            {/* Newsletter, social media, copyrgiht */}
            <div className={cn(styles.footerlast, "h-full")}>
              <div
                className={cn(
                  "flex flex-col basis-full",
                  styles.newsletterBox
                )}
              >
                <div
                  className={cn(
                    styles.newslettertext,
                    "mt-7 basis-full lg:mt-0"
                  )}
                >
                  <div className={styles.heading}>
                    {NEWSLETTER.heading}
                  </div>
                </div>

                <NewsletterForm
                  data={NEWSLETTER.form}
                  styles={styles}
                />
              </div>

              <div className={styles.socialMedia}>
                {footer?.social ? (
                  <>
                    <div className="mt-2">
                      <div className="inline-grid grid-cols-5 gap-3">
                        {footer.social.links.map(
                          (item, i) => (
                            <a
                              href={item.url}
                              target={item?.target}
                              key={i}
                              onClick={() =>
                                GAEventsLogoHander({ title: item?.title })
                              }
                              id={"social-media-icon-"+ item.title}
                              aria-label={`Amity-${item.title}`}
                            >
                              {item.iconUrl ? (
                                <UnivoImage
                                  src={item.iconUrl}
                                  alt={item.alt || item.title}
                                  width={20}
                                  height={20}
                                  sizes="20px"
                                  className="h-5 w-5 object-contain"
                                  unoptimized
                                />
                              ) : (
                                SocialLogo[item?.title]
                              )}
                            </a>
                          )
                        )}
                      </div>
                    </div>
                  </>
                ) : null}

                {footer?.copyrightHtml ? (
                  <div
                    className={cn("mt-2 mb-8 lining-nums", styles.copyright)}
                    dangerouslySetInnerHTML={{ __html: footer.copyrightHtml }}
                  />
                ) : null}
              </div>
            </div>
          </div>
        </Card>
      </footer>
    </>
  );
}
